import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, X, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const DOC_TYPES = [
    "SOP",
    "Work Instruction",
    "Process Document",
    "Policy",
    "Equipment Manual",
    "Training Document",
    "Safety Procedure",
    "Other",
];
const ACCEPT = ".docx,.pdf,.pptx,.png,.jpg,.jpeg";

export default function Upload() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [file, setFile] = useState(null);
    const [type, setType] = useState("Work Instruction");
    const [department, setDepartment] = useState("");
    const [owner, setOwner] = useState(user?.id || "");
    const [showDetails, setShowDetails] = useState(false);
    const [description, setDescription] = useState("");
    const [tagText, setTagText] = useState("");
    const [tags, setTags] = useState([]);
    const [users, setUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [quota, setQuota] = useState(null);
    const dropRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        api.get("/users")
            .then((r) => setUsers(r.data.filter((u) => u.is_active)))
            .catch(() => {});
        api.get("/documents/upload-quota")
            .then((r) => setQuota(r.data))
            .catch(() => {});
    }, []);

    const addTag = (raw) => {
        const t = raw.trim().replace(/,+$/, "");
        if (!t) return;
        setTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
        setTagText("");
    };
    const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

    const onDrop = (e) => {
        e.preventDefault();
        dropRef.current?.classList.remove("ring-2");
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadFile(f);
    };

    const submit = (e) => {
        e.preventDefault();
        if (!file) {
            toast.error("Choose a file first");
            return;
        }
        uploadFile(file);
    };

    const uploadFile = async (f) => {
        setFile(f);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("type", type);
        if (department) fd.append("department", department);
        if (owner) fd.append("owner_user_id", owner);
        if (description) fd.append("description", description);
        if (tags.length) fd.append("tags", JSON.stringify(tags));
        setBusy(true);
        try {
            const { data } = await api.post("/documents", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Uploaded — parsing in background");
            nav(`/knowledge/documents?highlight=${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="upload-page" className="space-y-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                        Knowledge
                    </div>
                    <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                        Upload document
                    </h1>
                    <p className="mt-1.5 text-sm text-neutral-500">
                        Drop a file and you're done. Word documents are split into sections and
                        procedure steps automatically.
                    </p>
                </div>
                {quota && !quota.unlimited && (
                    <div
                        data-testid="upload-quota-counter"
                        className="shrink-0 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-right"
                    >
                        <div className="text-xs font-semibold text-neutral-900">
                            {quota.additional_used} of {quota.max_additional_uploaded_documents} additional uploads used
                        </div>
                        <div className="mt-0.5 text-[10px] text-neutral-500">
                            Seed documents don't count against your quota.
                        </div>
                    </div>
                )}
            </div>

            <form onSubmit={submit} className="space-y-8">
                <div
                    ref={dropRef}
                    onDragOver={(e) => {
                        e.preventDefault();
                        dropRef.current?.classList.add("ring-2");
                    }}
                    onDragLeave={() => dropRef.current?.classList.remove("ring-2")}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    data-testid="upload-dropzone"
                    className="rounded-lg border border-dashed border-neutral-300 bg-white p-10 text-center ring-neutral-900/30 transition hover:border-neutral-500 cursor-pointer"
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPT}
                        data-testid="upload-file-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }}
                        className="hidden"
                    />
                    {file ? (
                        <div className="flex flex-col items-center gap-2">
                            <FileText size={22} className="text-neutral-500" />
                            <div data-testid="upload-file-name" className="text-sm font-medium text-neutral-900">
                                {file.name}
                            </div>
                            <div className="text-[11px] text-neutral-500">
                                {(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFile(null);
                                }}
                                className="text-xs"
                            >
                                Choose a different file
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <UploadCloud size={22} className="text-neutral-500" />
                            <div className="text-sm font-medium text-neutral-900">
                                {busy ? "Uploading…" : "Drop a file here, or click to browse"}
                            </div>
                            <div className="text-[11px] text-neutral-500">
                                .docx, .pdf, .pptx, .png, .jpg — up to 25 MB. Uploads start right away.
                            </div>
                        </div>
                    )}
                </div>

                <button type="button" data-testid="upload-details-toggle" onClick={() => setShowDetails((s) => !s)}
                    className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900">
                    {showDetails ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Extra details (optional) — set these before dropping a file
                </button>

                {showDetails && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="upload-details">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Document type</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger data-testid="upload-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {DOC_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                        {t}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">Department</Label>
                        <Input
                            data-testid="upload-department"
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            placeholder="e.g. Quality Control"
                        />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Document owner (defaults to you)</Label>
                        <Select value={owner} onValueChange={setOwner}>
                            <SelectTrigger data-testid="upload-owner">
                                <SelectValue placeholder="Select an owner" />
                            </SelectTrigger>
                            <SelectContent>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                        {u.name} · {u.email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Description (optional)</Label>
                        <Textarea
                            data-testid="upload-description"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs">Tags (optional)</Label>
                        <div className="flex flex-wrap items-center gap-2 rounded-md border border-neutral-200 bg-white px-2 py-1.5">
                            {tags.map((t) => (
                                <span
                                    key={t}
                                    data-testid={`tag-${t}`}
                                    className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
                                >
                                    {t}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(t)}
                                        className="text-neutral-500 hover:text-neutral-900"
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                            <input
                                data-testid="upload-tag-input"
                                value={tagText}
                                onChange={(e) => setTagText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                        e.preventDefault();
                                        addTag(tagText);
                                    } else if (e.key === "Backspace" && !tagText && tags.length) {
                                        removeTag(tags[tags.length - 1]);
                                    }
                                }}
                                placeholder="Type and press Enter"
                                className="flex-1 min-w-[140px] border-0 bg-transparent px-1 py-1 text-sm outline-none"
                            />
                        </div>
                    </div>
                </div>
                )}

                {file && !busy && (
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="upload-submit"
                        className="bg-neutral-900 text-white hover:bg-neutral-800"
                    >
                        Try upload again
                    </Button>
                </div>
                )}
            </form>
        </div>
    );
}
